import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Container, Scale, Boxes } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { SPECIES_CATALOG } from "../data/species";
import { scrollToSection } from "./SmoothScroll";
import { useLang } from "../i18n";

// internal volume (m³) and typical payload (kg)
const CONTAINERS = [
  { code: "20ft", volume: 33.2, payload: 25000 },
  { code: "40ft", volume: 67.7, payload: 26500 },
  { code: "40ft HC", volume: 76.4, payload: 26500 },
];
const STOWAGE = 0.85; // usable share of internal volume for packed lumber
const BF_PER_M3 = 424;

const densityOf = (s) => parseInt(String(s.properties.density).replace(/[^0-9]/g, ""), 10) || 500;

const selectCls =
  "w-full appearance-none rounded-none border-0 border-b border-border bg-transparent px-0 py-3 font-serif text-2xl text-brand-blue focus:border-brand-timber focus:outline-none";

export const LoadCalculator = () => {
  const { t } = useLang();
  const [containerIdx, setContainerIdx] = useState(1);
  const [slug, setSlug] = useState("white-oak");

  const species = SPECIES_CATALOG.find((s) => s.slug === slug);
  const container = CONTAINERS[containerIdx];
  const density = densityOf(species);

  const usableVolume = container.volume * STOWAGE;
  const weightLimited = container.payload / density;
  const netVolume = Math.min(usableVolume, weightLimited);
  const boardFeet = Math.round((netVolume * BF_PER_M3) / 100) * 100;
  const weightBound = weightLimited < usableVolume;

  const goQuote = () => scrollToSection("#quote");

  return (
    <section
      id="calculator"
      data-testid="load-calculator"
      className="bg-brand-blue py-28 text-brand-bone md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="mb-14"
        >
          <motion.span variants={fadeUp} className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber">
            {t("calc.kicker")}
          </motion.span>
          <motion.h2 variants={fadeUp} className="mt-4 max-w-3xl font-serif text-5xl font-medium leading-[0.95] tracking-tight md:text-6xl">
            {t("calc.title")}
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-6 max-w-md font-light text-base leading-relaxed text-brand-bone/70">
            {t("calc.sub")}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Inputs */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-5"
          >
            <motion.div variants={fadeUp} className="mb-10">
              <label className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/60">
                {t("calc.container")}
              </label>
              <div className="flex gap-2">
                {CONTAINERS.map((c, i) => (
                  <button
                    key={c.code}
                    data-testid={`calc-container-${i}`}
                    onClick={() => setContainerIdx(i)}
                    className={`rounded-full border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                      containerIdx === i
                        ? "border-brand-timber bg-brand-timber text-brand-bone"
                        : "border-brand-bone/30 text-brand-bone/80 hover:border-brand-timber hover:text-brand-timber"
                    }`}
                  >
                    {c.code}
                  </button>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp}>
              <label htmlFor="calc-species" className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/60">
                {t("calc.species")}
              </label>
              <select
                id="calc-species"
                data-testid="calc-species-select"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className={selectCls + " border-brand-bone/25 bg-brand-blue text-brand-bone"}
              >
                {SPECIES_CATALOG.map((s) => (
                  <option key={s.slug} value={s.slug} className="bg-brand-bone text-brand-blue">
                    {`${s.name} — ${s.properties.density}`}
                  </option>
                ))}
              </select>
            </motion.div>

            <motion.p variants={fadeUp} className="mt-10 max-w-md text-xs font-light leading-relaxed text-brand-bone/50">
              {t("calc.note")}
            </motion.p>
          </motion.div>

          {/* Results */}
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-6 lg:col-start-7"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <motion.div variants={fadeUp} className="rounded-sm border border-brand-bone/20 bg-brand-bone/[0.04] p-8 backdrop-blur-sm">
                <Boxes size={24} className="text-brand-timber" strokeWidth={1.5} />
                <div data-testid="calc-result-bf" className="mt-5 font-serif text-5xl font-medium leading-none md:text-6xl">
                  {boardFeet.toLocaleString()}
                </div>
                <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/60">
                  {t("calc.boardFeet")}
                </div>
              </motion.div>
              <motion.div variants={fadeUp} className="rounded-sm border border-brand-bone/20 bg-brand-bone/[0.04] p-8 backdrop-blur-sm">
                <Container size={24} className="text-brand-timber" strokeWidth={1.5} />
                <div data-testid="calc-result-volume" className="mt-5 font-serif text-5xl font-medium leading-none md:text-6xl">
                  {netVolume.toFixed(1)} m³
                </div>
                <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-bone/60">
                  {t("calc.netVolume")}
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="mt-4 flex items-center gap-3 rounded-sm border border-brand-bone/20 bg-brand-bone/[0.04] px-6 py-4">
              <Scale size={18} className="shrink-0 text-brand-timber" strokeWidth={1.5} />
              <p data-testid="calc-limit" className="font-mono text-[11px] uppercase tracking-[0.15em] text-brand-bone/70">
                {t("calc.limit")}: {weightBound ? t("calc.byWeight") : t("calc.byVolume")}
                {" · "}
                {weightBound
                  ? `${container.payload.toLocaleString()} kg`
                  : `${usableVolume.toFixed(1)} m³`}
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-8">
              <button
                data-testid="calc-quote-btn"
                onClick={goQuote}
                className="group inline-flex items-center gap-3 rounded-full bg-brand-timber px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-bone transition-colors duration-300 hover:bg-brand-bone hover:text-brand-blue"
              >
                {t("calc.cta")}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
